import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { FiatCurrency, InvoiceStatus, InvoiceSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Amount, CryptoFiatAmount, formatCryptoAmount, formatFiatAmount, formatUsdAmount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { INVOICE_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { exportCsv } from "@/lib/csv";
import { cn, formatStatusLabel } from "@/lib/utils";
import { getInvoiceAssetOptions, listInvoices } from "@/api/invoices";
import { listMerchants } from "@/api/merchants";
import { type InvoiceListItem, type ListInvoicesParams } from "@/api/types";

// Real implementation of Task 26 Part C: the invoices list, upgraded to the
// shared DataTable + `lib/table-state.ts`'s `useTableState` — the first
// Phase-4 list page to use it exactly as designed, because `GET /v1/invoices`
// genuinely supports server-side pagination/sort/filter (unlike Task 25's
// merchants list). URL-persisted filters/sort/page via `validateSearch`
// below. Row detail is now a real route (`/invoices/$invoiceId`, Part D)
// rather than the old in-page `InvoiceDetailDialog` — that dialog is removed
// entirely; its content moved to the new detail page.
// A `type` alias (not `interface`) — `useTableState<TFilters>`'s constraint
// is `Record<string, unknown>`, which only a type alias's implicit index
// signature satisfies; an interface with only named properties doesn't.
type InvoiceFilters = {
  status?: InvoiceStatus;
  subStatus?: InvoiceSubStatus;
  merchantId?: string;
  assetId?: string;
  fiatCurrency?: FiatCurrency;
  amountUsdMin?: string;
  amountUsdMax?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: InvoiceFilters = {};

// Which filter keys live in the "More filters" dialog rather than the
// always-visible Status select — used to size the dialog-trigger's active
// count badge (Status has its own visible value, so it isn't counted here).
const DIALOG_FILTER_KEYS = [
  "subStatus",
  "merchantId",
  "assetId",
  "fiatCurrency",
  "amountUsdMin",
  "amountUsdMax",
  "from",
  "to",
] as const satisfies ReadonlyArray<keyof InvoiceFilters>;

/** Plain type-guarded passthrough — mirrors `_app.merchants.$merchantId.tsx`'s `validateSearch` house style. */
function validateInvoicesSearch(search: Record<string, unknown>): TableSearch<InvoiceFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as InvoiceStatus) : undefined,
    subStatus: typeof search.subStatus === "string" ? (search.subStatus as InvoiceSubStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    fiatCurrency: typeof search.fiatCurrency === "string" ? (search.fiatCurrency as FiatCurrency) : undefined,
    amountUsdMin: typeof search.amountUsdMin === "string" ? search.amountUsdMin : undefined,
    amountUsdMax: typeof search.amountUsdMax === "string" ? search.amountUsdMax : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/invoices")({
  validateSearch: validateInvoicesSearch,
  component: InvoicesPage,
});

// ─── formatting helpers (shared by table cells and CSV export) ─────────────

// Date + time, matching the merchant portal's invoices list — used to only
// show the date, but "Created" is also sortable and worth the extra
// precision.
function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

const INPUT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

// ─── row → detail navigation ─────────────────────────────────────────────────
//
// DataTable has no onRowClick concept (out of scope to modify), so every
// cell's content is wrapped in a <Link> that fills the cell — same pattern
// Task 25's merchants list established.

function RowLink({ invoiceId, children }: { invoiceId: string; children: ReactNode }) {
  return (
    <Link to="/invoices/$invoiceId" params={{ invoiceId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

// ─── "More filters" dialog ───────────────────────────────────────────────────
//
// Everything except Status (kept as an always-visible quick filter in the
// toolbar) lives behind this one button instead of nine controls competing
// for space inline — the trigger's badge shows how many of them are active
// so the collapsed state never hides an applied filter silently.

function InvoiceFiltersDialog({
                                filters,
                                setFilter,
                                merchants,
                                assetOptions,
                              }: {
  filters: InvoiceFilters;
  setFilter: <K extends keyof InvoiceFilters>(key: K, value: InvoiceFilters[K]) => void;
  merchants: Array<{ id: string; businessName: string }>;
  assetOptions: Array<{ id: string; symbol: string; network: string }>;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = DIALOG_FILTER_KEYS.filter((key) => filters[key] !== undefined).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5"/>
          Filters
          {activeCount > 0 && (
            <span
              className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Filter invoices</DialogTitle>
          <DialogDescription>Narrow the list by merchant, asset, currency, amount, or date.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="filter-subStatus">Sub-status</Label>
            <select
              id="filter-subStatus"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.subStatus ?? "ALL"}
              onChange={(e) => setFilter("subStatus", e.target.value === "ALL" ? undefined : (e.target.value as InvoiceSubStatus))}
            >
              <option value="ALL">All sub-statuses</option>
              {Object.values(InvoiceSubStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-merchant">Merchant</Label>
            <select
              id="filter-merchant"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.merchantId ?? "ALL"}
              onChange={(e) => setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All merchants</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.businessName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-asset">Asset</Label>
            <select
              id="filter-asset"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.assetId ?? "ALL"}
              onChange={(e) => setFilter("assetId", e.target.value === "ALL" ? undefined : e.target.value)}
            >
              <option value="ALL">All assets</option>
              {assetOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.symbol} · {formatStatusLabel(a.network)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-currency">Fiat currency</Label>
            <select
              id="filter-currency"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={filters.fiatCurrency ?? "ALL"}
              onChange={(e) =>
                setFilter("fiatCurrency", e.target.value === "ALL" ? undefined : (e.target.value as FiatCurrency))
              }
            >
              <option value="ALL">All currencies</option>
              {Object.values(FiatCurrency).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-min">Min amount (USD)</Label>
            <input
              id="filter-min"
              placeholder="No minimum"
              className={INPUT_CLASSNAME}
              value={filters.amountUsdMin ?? ""}
              onChange={(e) => setFilter("amountUsdMin", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-max">Max amount (USD)</Label>
            <input
              id="filter-max"
              placeholder="No maximum"
              className={INPUT_CLASSNAME}
              value={filters.amountUsdMax ?? ""}
              onChange={(e) => setFilter("amountUsdMax", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-from">From date</Label>
            <input
              id="filter-from"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.from ?? ""}
              onChange={(e) => setFilter("from", e.target.value || undefined)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filter-to">To date</Label>
            <input
              id="filter-to"
              type="date"
              className={INPUT_CLASSNAME}
              value={filters.to ?? ""}
              onChange={(e) => setFilter("to", e.target.value || undefined)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={activeCount === 0}
            onClick={() => DIALOG_FILTER_KEYS.forEach((key) => setFilter(key, undefined))}
          >
            Clear these filters
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── invoices page ───────────────────────────────────────────────────────────

function InvoicesPage() {
  const tableState = useTableState<InvoiceFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListInvoicesParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListInvoicesParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["invoices", params],
    queryFn: () => listInvoices(params),
  });

  // Merchant filter is a name dropdown (not a raw UUID input) — reuses Task
  // 14b's listMerchants rather than duplicating it.
  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) =>
    a.businessName.localeCompare(b.businessName),
  );

  // Task 26 Part A's new INVOICES_VIEWER-scoped read — the asset filter
  // dropdown, unlike `GET /v1/assets`, doesn't require CONFIG_MANAGER.
  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<InvoiceListItem>[] = [
    {
      key: "publicId",
      header: "Invoice ID",
      cell: (inv) => <RowLink invoiceId={inv.id}><span className="group-hover:underline">{inv.publicId}</span></RowLink>,
    },
    {
      key: "merchant",
      header: "Merchant",
      cell: (inv) => (
        <MerchantCell merchantId={inv.merchant.id} businessName={inv.merchant.businessName} publicId={inv.merchant.publicId} />
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          {/* subStatus, when present, only ever pairs with ACTION_REQUIRED
              and already fully explains the state — showing both would just
              repeat "Action required" next to its own reason. */}
          <StatusBadge
            label={formatStatusLabel(inv.subStatus ?? inv.status)}
            color={INVOICE_STATUS_COLOR[inv.status]}
          />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <AssetLabel symbol={inv.asset.symbol} network={inv.asset.network}/>
        </RowLink>
      ),
    },
    {
      key: "fiatAmount",
      header: "Paid",
      sortable: true,
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <CryptoFiatAmount
            native={inv.cryptoAmount}
            symbol={inv.asset.symbol}
            fiatAmount={inv.fiatAmount}
            fiatCurrency={inv.fiatCurrency}
          />
        </RowLink>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <span className="block text-sm text-foreground">
            <Amount native={inv.cryptoFee} symbol={inv.asset.symbol} usd={inv.feeUsd}/>
          </span>
        </RowLink>
      ),
    },
    {
      key: "merchantAmount",
      header: "Settlement",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <span className="block text-sm text-foreground">
            <Amount native={inv.merchantAmount} symbol={inv.asset.symbol} usd={inv.merchantAmountUsd}/>
          </span>
        </RowLink>
      ),
    },
    {
      key: "txHash",
      header: "Tx hash",
      // Deliberately NOT a RowLink here: an explorer link + copy button are
      // this cell's own interactive affordances, distinct from the row's
      // own detail-page destination — same convention as the merchant
      // portal's invoices list.
      cell: (inv) => <HashLink value={inv.txHash} type="tx" network={inv.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (inv) => <RowLink invoiceId={inv.id}>{formatDate(inv.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load invoices." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        csvExport={{
          onExport: () =>
            exportCsv({
              columns: [
                { header: "Invoice ID", value: (inv: InvoiceListItem) => inv.publicId },
                { header: "Merchant", value: (inv: InvoiceListItem) => inv.merchant.businessName },
                { header: "Asset", value: (inv: InvoiceListItem) => inv.asset.symbol },
                { header: "Network", value: (inv: InvoiceListItem) => inv.asset.network },
                { header: "Status", value: (inv: InvoiceListItem) => inv.status },
                { header: "Sub-status", value: (inv: InvoiceListItem) => inv.subStatus ?? "" },
                {
                  header: "Fiat amount",
                  value: (inv: InvoiceListItem) => formatFiatAmount(inv.fiatAmount, inv.fiatCurrency),
                },
                {
                  header: "Crypto amount",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.cryptoAmount, inv.asset.symbol),
                },
                {
                  header: "Fee",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.cryptoFee, inv.asset.symbol),
                },
                { header: "Fee (USD)", value: (inv: InvoiceListItem) => formatUsdAmount(inv.feeUsd) },
                {
                  header: "Settlement",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.merchantAmount, inv.asset.symbol),
                },
                {
                  header: "Settlement (USD)",
                  value: (inv: InvoiceListItem) => formatUsdAmount(inv.merchantAmountUsd),
                },
                { header: "Tx hash", value: (inv: InvoiceListItem) => inv.txHash ?? "" },
                { header: "Created", value: (inv: InvoiceListItem) => formatDate(inv.createdAt) },
              ],
              fetchPage: (page, pageSize) =>
                listInvoices({
                  ...tableState.filters,
                  sortBy: (tableState.sortBy ?? undefined) as ListInvoicesParams["sortBy"],
                  sortOrder: tableState.sortOrder,
                  page,
                  pageSize,
                }).then((r) => ({ data: r.data, total: r.total })),
              filenamePrefix: "invoices",
            }),
        }}
        filterBar={
          <>
            <select
              aria-label="Status"
              className={SELECT_CLASSNAME}
              value={tableState.filters.status ?? "ALL"}
              onChange={(e) =>
                tableState.setFilter(
                  "status",
                  e.target.value === "ALL" ? undefined : (e.target.value as InvoiceStatus),
                )
              }
            >
              <option value="ALL">All statuses</option>
              {Object.values(InvoiceStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>

            <InvoiceFiltersDialog
              filters={tableState.filters}
              setFilter={tableState.setFilter}
              merchants={sortedMerchants}
              assetOptions={assetOptions ?? []}
            />

            {anyFilterActive && (
              <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                Clear filters
              </Button>
            )}
          </>
        }
        getRowKey={(inv) => inv.id}
        emptyMessage="No invoices match your filters."
      />
    </div>
  );
}
